// src/components/payment/payment-component.tsx
"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { env } from "@/lib/env";
import { useLocale, useTranslations } from "next-intl";
import type { PaymentResponse } from "./types";
import styles from "./payment-component.module.css";
// 🚀 ADDED IoCloseOutline for the cancel state
import { IoCheckmark, IoCloseOutline } from "react-icons/io5";

const PAYMENTS_ENDPOINT_URL = env.NEXT_PAYMENTS_API_URL;

interface PaymentOutcomeProps {
  status: "success" | "cancel" | string; // 🚀 ADDED status
  redirectUrl: string;
  redirectLabel: string;
  retryUrl?: string; // 🚀 ADDED retryUrl
}

export default function PaymentOutcomeComponent({
  status,
  redirectUrl,
  redirectLabel,
  retryUrl,
}: PaymentOutcomeProps) {
  const params = useSearchParams();
  const locale = useLocale();
  const t = useTranslations("PaymentSuccess");

  const isCancel = status === "cancel";
  const paymentId = params.get("payment_intent") || params.get("session_id");

  const [details, setDetails] = useState<PaymentResponse | null>(null);
  const [loading, setLoading] = useState(!isCancel); // Don't load if cancelled
  const [countdown, setCountdown] = useState(30);

  const handleRedirect = () => {
    window.location.href = redirectUrl;
  };

  // ⏱️ Countdown Timer (Only auto-redirect on success)
  useEffect(() => {
    if (isCancel) return; // Don't force redirect on cancel
    if (countdown <= 0) {
      handleRedirect();
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, redirectUrl, isCancel]);

  // 📡 Fetch Payment Data
  useEffect(() => {
    async function fetchData() {
      if (isCancel) return; // 🚀 Skip fetch if cancelled

      if (!paymentId) {
        setTimeout(() => setLoading(false), 1000);
        return;
      }

      try {
        const paramName = params.get("payment_intent")
          ? "payment_intent_id"
          : "session_id";

        const res = await fetch(
          `${PAYMENTS_ENDPOINT_URL}/getPaymentSuccess?${paramName}=${paymentId}&locale=${locale}`,
        );

        if (!res.ok) throw new Error("Request failed");

        const data: PaymentResponse = await res.json();
        setDetails(data);
      } catch (err) {
        console.error("Payment fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [paymentId, locale, params, isCancel]);

  // ❌ --- Cancel State --- ❌
  if (isCancel) {
    return (
      <main className={styles.container}>
        <div className={styles.card}>
          <div className={`${styles.iconWrapper} ${styles.cancelIconWrapper}`}>
            <IoCloseOutline className={styles.cancelIcon} />
          </div>
          <h1 className={styles.title}>
            {t("cancelTitle") || "Payment Cancelled"}
          </h1>
          <p className={styles.subtitle}>
            {t("cancelSubtitle") ||
              "Your transaction was cancelled and you haven't been charged."}
          </p>

          <div className={styles.actions}>
            {retryUrl && (
              <button
                onClick={() => (window.location.href = retryUrl)}
                className={styles.buttonPrimary}
              >
                {t("tryAgain") || "Try Again"}
              </button>
            )}
            <button onClick={handleRedirect} className={styles.buttonSecondary}>
              {redirectLabel}
            </button>
          </div>
        </div>
      </main>
    );
  }

  // --- Loading State ---
  if (loading) {
    return (
      <main className={styles.container}>
        <div className={styles.skeletonCard}>
          <div className={styles.skeletonCircle} />
          <div className={`${styles.skeletonLine} ${styles.short}`} />
          <div className={styles.skeletonLine} />
          <div className={styles.skeletonLine} />
        </div>
      </main>
    );
  }

  // --- Error / No Details State ---
  if (!details) {
    return (
      <main className={styles.container}>
        <div className={styles.card}>
          <div className={styles.iconWrapper}>
            <IoCheckmark className={styles.successIcon} />
          </div>
          <h1 className={styles.title}>{t("title")}</h1>
          <p className={styles.subtitle}>{t("thankYou")}</p>
          <button onClick={handleRedirect} className={styles.buttonPrimary}>
            {redirectLabel}
          </button>
          <p className={styles.redirectText}>
            {t("redirect")} {countdown}s...
          </p>
        </div>
      </main>
    );
  }

  // --- Success State ---
  const { payment, listing } = details;

  return (
    <main className={styles.container}>
      <div className={styles.card}>
        <div className={styles.iconWrapper}>
          <IoCheckmark className={styles.successIcon} />
        </div>
        <h1 className={styles.title}>{t("title")}</h1>
        <p className={styles.subtitle}>
          {listing ? t("subscriptionActive") : t("paymentComplete")}
        </p>

        {/* ✅ Receipt Box */}
        <div className={styles.receipt}>
          {listing && (
            <div className={styles.receiptRow}>
              <span className={styles.label}>{t("item")}</span>
              <span>{t("listingSubscription")}</span>
            </div>
          )}

          <div className={styles.receiptRow}>
            <span className={styles.label}>{t("date")}</span>
            <span>{new Date().toLocaleDateString()}</span>
          </div>

          <div className={styles.receiptRow}>
            <span className={styles.label}>{t("method")}</span>
            <span>
              {payment.brand?.toUpperCase()} •••• {payment.last4}
            </span>
          </div>

          <div className={styles.receiptRow}>
            <span className={styles.label}>{t("totalPaid")}</span>
            <span>€{payment.amount_paid.toFixed(2)}</span>
          </div>
        </div>

        <div className={styles.actions}>
          <button onClick={handleRedirect} className={styles.buttonPrimary}>
            {redirectLabel}
          </button>
        </div>
        <p className={styles.redirectText}>
          {t("redirect")} {countdown}s...
        </p>
      </div>
    </main>
  );
}
